import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorToMessage } from "@/lib/apiClient";
import Modal from "@/components/Modal";
import { EmptyState, ErrorMessage, PageHeader, Pagination, SearchInput, Toolbar } from "@/components/DataTableParts";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { canProcure, useAuth } from "@/lib/auth";

const ENDPOINT = "/vendors";
const EMPTY = { name: "", gstin: "", contact_name: "", mobile: "", email: "", address: "", default_warranty_days: 30, is_active: true };

export default function VendorsPage() {
  const { user } = useAuth();
  const canEdit = canProcure(user);
  // V2.0 — Only SUPER_ADMIN / ADMIN can delete vendors (matches backend gate).
  const canDelete = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  useEffect(() => { const t = setTimeout(() => setQDeb(q), 300); return () => clearTimeout(t); }, [q]);
  const [editing, setEditing] = useState(null); // null | "new" | vendor obj
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // V2.0 — selection + delete state
  const [selected, setSelected] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // vendor object | "bulk" | null
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [bulkResult, setBulkResult] = useState(null); // { deleted:[], blocked:[{id,reason}] }

  const params = useMemo(() => ({ page, page_size: 20, ...(qDeb ? { q: qDeb } : {}) }), [page, qDeb]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get(ENDPOINT, { params })).data); }
    catch (err) { if (process.env.NODE_ENV === "development") console.warn("VendorsPage.load failed", err?.message || err); setData({ items: [], meta: null }); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setFormError(""); };
  const openEdit = (v) => {
    setForm({
      name: v.name, gstin: v.gstin || "", contact_name: v.contact_name || "",
      mobile: v.mobile || "", email: v.email || "", address: v.address || "",
      default_warranty_days: v.default_warranty_days, is_active: v.is_active,
    });
    setEditing(v);
    setFormError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setFormError("");
    try {
      const payload = { ...form };
      if (!payload.gstin) payload.gstin = null;
      if (!payload.email) payload.email = null;
      if (!payload.mobile) payload.mobile = null;
      if (!payload.contact_name) payload.contact_name = null;
      if (!payload.address) payload.address = null;
      if (editing === "new") await api.post(ENDPOINT, payload);
      else await api.patch(`${ENDPOINT}/${editing.id}`, payload);
      setEditing(null);
      await load();
    } catch (err) { setFormError(errorToMessage(err)); }
    finally { setSaving(false); }
  };

  // V2.0 — Selection helpers
  const visibleIds = useMemo(() => (data?.items || []).map((v) => v.id), [data]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  // Whenever the page/filter reloads, forget selections that are no longer visible
  // (keep it simple; user can re-tick on the new page).
  useEffect(() => { setSelected(new Set()); setBulkResult(null); }, [qDeb, page]);

  const askDelete = (v) => { setDeleteTarget(v); setDeleteError(""); setBulkResult(null); };
  const askBulkDelete = () => {
    if (selected.size === 0) return;
    setDeleteTarget("bulk"); setDeleteError(""); setBulkResult(null);
  };
  const closeDelete = () => {
    setDeleteTarget(null); setDeleteError(""); setBulkResult(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError("");
    try {
      if (deleteTarget === "bulk") {
        const ids = Array.from(selected);
        const { data: res } = await api.post(`${ENDPOINT}/bulk-delete`, { ids });
        setBulkResult(res);
        // Drop successfully-deleted rows from selection.
        setSelected((prev) => {
          const next = new Set(prev);
          (res.deleted || []).forEach((id) => next.delete(id));
          return next;
        });
        await load();
        // If everything succeeded, close the dialog automatically.
        if ((res.blocked || []).length === 0) {
          setDeleteTarget(null);
        }
      } else {
        await api.delete(`${ENDPOINT}/${deleteTarget.id}`);
        setDeleteTarget(null);
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(deleteTarget.id);
          return next;
        });
        await load();
      }
    } catch (err) {
      setDeleteError(errorToMessage(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Vendors"
        subtitle="Suppliers of refurbished laptops. Reception and Admin can add or edit."
        actions={canEdit ? (
          <div className="flex items-center gap-2">
            {canDelete && selected.size > 0 && (
              <button onClick={askBulkDelete} data-testid="btn-bulk-delete-vendors"
                className="inline-flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-800 px-3 py-1.5 text-sm">
                <Trash2 size={14} /> Delete Selected ({selected.size})
              </button>
            )}
            <button onClick={openNew} data-testid="btn-new-vendor"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm">
              <Plus size={14} /> Add Vendor
            </button>
          </div>
        ) : null}
      />
      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search vendors…" />
      </Toolbar>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="vendors-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr>
              {canDelete && (
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label="Select all vendors on this page"
                    data-testid="chk-select-all-vendors"
                  />
                </th>
              )}
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">GSTIN</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-right px-3 py-2">Warranty (d)</th>
              <th className="text-left px-3 py-2">Status</th>
              {canEdit && <th className="text-right px-3 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500"><Loader2 className="inline animate-spin" size={16}/> Loading…</td></tr>
            ) : data?.items?.length ? data.items.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vendor-${v.id}`}>
                {canDelete && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(v.id)}
                      onChange={() => toggleOne(v.id)}
                      aria-label={`Select vendor ${v.name}`}
                      data-testid={`chk-vendor-${v.id}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2 font-medium">{v.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{v.gstin || "—"}</td>
                <td className="px-3 py-2">{v.contact_name || "—"}<div className="text-xs text-slate-400 font-mono">{v.mobile || ""}</div></td>
                <td className="px-3 py-2 text-right tabular-nums">{v.default_warranty_days}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${v.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"}`}>
                    {v.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 hover:bg-slate-100 px-2 py-1 text-xs">
                      <Pencil size={12}/> Edit
                    </button>
                    {canDelete && (
                      <button onClick={() => askDelete(v)} data-testid={`delete-vendor-${v.id}`}
                        className="ml-2 inline-flex items-center gap-1 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 px-2 py-1 text-xs">
                        <Trash2 size={12}/> Delete
                      </button>
                    )}
                  </td>
                )}
              </tr>
            )) : (
              <tr><td colSpan={7}><EmptyState label="No vendors yet. Click Add Vendor to create one." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination meta={data?.meta} page={page} onPage={setPage} />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "New Vendor" : "Edit Vendor"} size="md">
        {editing && (
          <form onSubmit={submit} className="space-y-3" data-testid="vendor-form">
            <ErrorMessage message={formError} />
            <Field label="Vendor Name *"><input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="field-name"/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="GSTIN"><input className="input" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} maxLength={15}/></Field>
              <Field label="Default Warranty (days)"><input type="number" min={0} max={3650} className="input" value={form.default_warranty_days} onChange={(e) => setForm({ ...form, default_warranty_days: Number(e.target.value) })}/></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact Name"><input className="input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}/></Field>
              <Field label="Mobile"><input className="input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}/></Field>
            </div>
            <Field label="Email"><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/></Field>
            <Field label="Address"><textarea rows={2} className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}/></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}/> Active</label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm rounded-md border border-slate-300">Cancel</button>
              <button type="submit" disabled={saving} data-testid="vendor-submit" className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={14}/> : null} Save
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* V2.0 — Delete confirmation (single + bulk) */}
      <Modal open={!!deleteTarget} onClose={closeDelete}
             title={deleteTarget === "bulk" ? "Delete Selected Vendors" : "Delete Vendor"}
             size="md">
        {deleteTarget && (
          <div className="space-y-3" data-testid="vendor-delete-dialog">
            {deleteTarget === "bulk" ? (
              <p className="text-sm text-slate-700">
                You are about to delete <span className="font-semibold">{selected.size}</span> vendor(s).
                Vendors referenced by Purchases or Vendor Cases will be skipped
                and reported below.
              </p>
            ) : (
              <p className="text-sm text-slate-700">
                Delete vendor <span className="font-semibold">{deleteTarget.name}</span>?
                This action cannot be undone.
              </p>
            )}
            <ErrorMessage message={deleteError} />
            {bulkResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-2"
                   data-testid="vendor-bulk-result">
                <div>
                  <span className="font-semibold text-emerald-700">Deleted:</span>{" "}
                  {bulkResult.deleted?.length || 0} vendor(s)
                </div>
                {(bulkResult.blocked || []).length > 0 && (
                  <div>
                    <div className="font-semibold text-rose-700 mb-1">
                      Blocked ({bulkResult.blocked.length}):
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5">
                      {bulkResult.blocked.map((b) => {
                        const v = (data?.items || []).find((x) => x.id === b.id);
                        return (
                          <li key={b.id}>
                            <span className="font-medium">{v?.name || b.id}</span>: {b.reason}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={closeDelete}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300">
                {bulkResult ? "Close" : "Cancel"}
              </button>
              {!bulkResult && (
                <button type="button" onClick={confirmDelete} disabled={deleting}
                  data-testid="vendor-delete-confirm"
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-60">
                  {deleting ? <Loader2 className="animate-spin" size={14}/> : <Trash2 size={14}/>}
                  {deleteTarget === "bulk" ? "Delete Selected" : "Delete"}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs text-slate-600 mb-1">{label}</label>{children}</div>;
}
